import React from "react";
import { Body1Strong, Caption1, Radio, RadioGroup, Spinner } from "@fluentui/react-components";
import { CheckmarkCircle24Color, CircleRegular } from "@fluentui/react-icons";
import { useGetAvailableProfilesQuery } from "src/api/server-api";
import { useErrorContext, useProfileContext } from "src/providers";
import "./ProfileSelector.css";

import type { RadioGroupOnChangeData } from "@fluentui/react-components";
import type { ProfileSummary } from "src/types";

export const ProfileSelector: React.FC = () => {
  const { data: availableProfiles = [], isLoading, error } = useGetAvailableProfilesQuery();
  const { pendingSelection, stagedProfileName, handleSetProfile } = useProfileContext();
  const { validationError, unavailableStyles, setValidationError, setUnavailableStyles } =
    useErrorContext();

  const handleProfileChange = (
    ev: React.FormEvent<HTMLDivElement>,
    data: RadioGroupOnChangeData
  ) => {
    setValidationError(null);
    setUnavailableStyles(null);
    handleSetProfile(ev, data);
  };

  if (isLoading) {
    return (
      <div className="profile-selector-loading">
        <Spinner size="tiny" />
        <span>Loading profiles…</span>
      </div>
    );
  }

  if (error) {
    return <div className="profile-selector-error">Failed to load profiles</div>;
  }

  return (
    <div>
      <RadioGroup
        value={stagedProfileName ?? ""}
        onChange={handleProfileChange}
        disabled={pendingSelection !== null}
      >
        {availableProfiles.map((profile: ProfileSummary) => {
          const isSelected = stagedProfileName === profile.name;
          const isPending = pendingSelection === profile.name;

          return (
            <label key={profile.name} className="profile-card">
              <div className="profile-card-layout">
                {isSelected ? (
                  <CheckmarkCircle24Color />
                ) : isPending ? (
                  <div className="profile-card-circle-wrapper">
                    <Spinner size="tiny" />
                  </div>
                ) : (
                  <div className="profile-card-circle-wrapper">
                    <CircleRegular className="profile-card-circle" />
                  </div>
                )}
                <div className="profile-card-radio-wrapper">
                  <Radio
                    value={profile.name}
                    className="profile-card-radio"
                    onClick={() =>
                      isSelected &&
                      handleProfileChange({} as React.FormEvent<HTMLDivElement>, { value: "" })
                    }
                  />
                </div>
                <div className="profile-card-content">
                  <Body1Strong>{profile.display_name}</Body1Strong>
                  <Caption1>
                    {profile.description}. {profile.style_count} style
                    {profile.style_count !== 1 ? "s" : ""}.
                  </Caption1>
                </div>
              </div>
            </label>
          );
        })}
      </RadioGroup>

      {pendingSelection !== null && (
        <div className="profile-selector-validating">
          <Spinner size="tiny" />
          <span>Validating profile…</span>
        </div>
      )}

      {validationError && (
        <div className="profile-selector-error">
          <div>{validationError}</div>
          {unavailableStyles && unavailableStyles.length > 0 && (
            <div>
              <ul className="profile-selector-error-list">
                {unavailableStyles.slice(0, 3).map((style) => (
                  <li key={style.styleCode}>
                    {style.profileStyleName}{" "}
                    <code className="profile-selector-error-code">[{style.styleTag}]</code>
                  </li>
                ))}
                {unavailableStyles.length > 3 && (
                  <li className="profile-selector-error-more">
                    ... and {unavailableStyles.length - 3} more
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
